import { isValidOntarioHealthNumber, normalizeOntarioHealthNumber } from "@emr/contracts";
import type { IntakeData } from "../types";

/**
 * What the patient's phone read from the front of an Ontario photo health card. The text comes
 * from the on-device reader (healthCardReaderEngine.ts) and never leaves the phone: these values
 * only fill empty questionnaire fields, which the patient checks and submits as usual.
 *
 * Every value is kept only when it passes its check, so a reading never guesses:
 * - healthNumber: ten digits passing the Ontario check digit, read in the card's 4-3-3 groups.
 *   A reading that finds no such number, or two different ones, is no reading at all.
 * - versionCode: one or two letters printed right after the number, on the same line.
 * - birthDate, issueDate, expiryDate: real calendar dates printed YYYY-MM-DD, placed by their
 *   labels (BORN/NÉ(E), ISS/DÉL, EXP) or, when the labels are unreadable, by the card's layout
 *   (birth date first; issue and expiry side by side, issue first). A date that contradicts
 *   another (born after the card was issued, or expiring before it) is dropped.
 * The name is not read: nothing would use it.
 */
export interface HealthCardReading {
  readonly healthNumber: string;
  readonly versionCode?: string;
  readonly birthDate?: string;
  readonly issueDate?: string;
  readonly expiryDate?: string;
}

/** Letters the reader often returns for a digit (O for 0, I for 1, S for 5, and so on). */
const DIGIT_LOOKALIKES: Readonly<Record<string, string>> = {
  O: "0",
  Q: "0",
  D: "0",
  I: "1",
  L: "1",
  "|": "1",
  "!": "1",
  Z: "2",
  S: "5",
  G: "6",
  B: "8",
};

/** Digits the reader often returns for a letter of the version code. */
const LETTER_LOOKALIKES: Readonly<Record<string, string>> = {
  "0": "O",
  "1": "I",
  "2": "Z",
  "5": "S",
  "6": "G",
  "8": "B",
};

/** How the ten digits may come back: as printed (4-3-3), or with one or both gaps lost. */
const NUMBER_GROUPINGS: readonly (readonly number[])[] = [[4, 3, 3], [4, 6], [7, 3], [10]];

/** Of the ten digits, at least this many must be real digits rather than look-alike letters. */
const MIN_REAL_DIGITS = 7;

/** How a date may come back: as printed (YYYY - MM - DD), or with gaps lost. */
const DATE_GROUPINGS: readonly (readonly number[])[] = [[4, 2, 2], [4, 4], [8]];

interface Line {
  /** The line in capitals with accents and dash variants removed, for the labels. */
  readonly text: string;
  /** Its runs of letters and digits, in order; "?" (an unsure word) is a token of its own. */
  readonly tokens: readonly string[];
}

interface NumberMatch {
  readonly healthNumber: string;
  readonly versionCode?: string;
  readonly line: number;
  /** The tokens the number (and its version code) used, so no date reuses them. */
  readonly used: ReadonlySet<number>;
}

interface DateMatch {
  readonly value: string;
  readonly line: number;
  /** Its order within the line. */
  readonly index: number;
}

type DateKind = "birthDate" | "issueDate" | "expiryDate";

function lines(text: string): Line[] {
  return text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[\u2010-\u2015\u2212\ufe58\ufe63\uff0d]/g, "-")
    .split(/\r?\n/)
    .map((line) => ({ text: line, tokens: line.split(/[^0-9A-Z|!?]+/).filter(Boolean) }))
    .filter((line) => line.tokens.length > 0);
}

/** The token's digits, reading look-alike letters as digits; null when it is not all digits. */
function asDigits(token: string): { readonly digits: string; readonly real: number } | null {
  let digits = "";
  let real = 0;
  for (const character of token) {
    if (character >= "0" && character <= "9") {
      digits += character;
      real += 1;
      continue;
    }
    const digit = DIGIT_LOOKALIKES[character];
    if (digit === undefined) return null;
    digits += digit;
  }
  return { digits, real };
}

/** The token as a version code (one or two capital letters), or null. */
function asVersionCode(token: string): string | null {
  if (token.length < 1 || token.length > 2) return null;
  let code = "";
  for (const character of token) {
    const letter = character >= "A" && character <= "Z" ? character : LETTER_LOOKALIKES[character];
    if (letter === undefined) return null;
    code += letter;
  }
  return code;
}

/**
 * The digits of `count` tokens from `start` in the given grouping. The last group may carry the
 * version code stuck to it (the reader missed the gap): that tail is returned separately.
 */
function groupedDigits(
  tokens: readonly string[],
  start: number,
  grouping: readonly number[],
): { readonly digits: string; readonly real: number; readonly tail: string } | null {
  let digits = "";
  let real = 0;
  let tail = "";
  for (const [offset, size] of grouping.entries()) {
    const token = tokens[start + offset];
    if (token === undefined) return null;
    const last = offset === grouping.length - 1;
    const head = last ? token.slice(0, size) : token;
    if (head.length !== size || (!last && token.length !== size)) return null;
    const group = asDigits(head);
    if (group === null) return null;
    digits += group.digits;
    real += group.real;
    if (last) tail = token.slice(size);
  }
  return { digits, real, tail };
}

function findNumbers(found: readonly Line[]): NumberMatch[] {
  const matches: NumberMatch[] = [];
  for (const [lineIndex, line] of found.entries()) {
    const { tokens } = line;
    for (let start = 0; start < tokens.length; start += 1) {
      for (const grouping of NUMBER_GROUPINGS) {
        const group = groupedDigits(tokens, start, grouping);
        if (group === null || group.real < MIN_REAL_DIGITS) continue;
        if (!isValidOntarioHealthNumber(group.digits)) continue;
        const end = start + grouping.length;
        const used = new Set(
          Array.from({ length: grouping.length }, (_, offset) => start + offset),
        );
        let versionCode: string | null = null;
        if (group.tail !== "") {
          // Letters stuck to the last group are the version code; anything else (a digit too
          // many) means this was not the number.
          versionCode = /^[A-Z]{1,2}$/.test(group.tail) ? group.tail : null;
          if (versionCode === null) continue;
        } else {
          const next = tokens[end];
          versionCode = next === undefined ? null : asVersionCode(next);
          if (versionCode !== null) used.add(end);
        }
        matches.push({
          healthNumber: group.digits,
          ...(versionCode === null ? {} : { versionCode }),
          line: lineIndex,
          used,
        });
        break;
      }
    }
  }
  return matches;
}

/** YYYY-MM-DD when it is a real date between 1900 and 2099, else null. */
function calendarDate(digits: string): string | null {
  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));
  if (year < 1900 || year > 2099 || month < 1 || month > 12 || day < 1) return null;
  if (day > new Date(Date.UTC(year, month, 0)).getUTCDate()) return null;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

function findDates(found: readonly Line[], number: NumberMatch): DateMatch[] {
  const dates: DateMatch[] = [];
  for (const [lineIndex, line] of found.entries()) {
    const { tokens } = line;
    let index = 0;
    for (let start = 0; start < tokens.length; start += 1) {
      if (lineIndex === number.line && number.used.has(start)) continue;
      for (const grouping of DATE_GROUPINGS) {
        let digits = "";
        let real = 0;
        let fits = true;
        for (const [offset, size] of grouping.entries()) {
          const token = tokens[start + offset];
          const group = token?.length === size ? asDigits(token) : null;
          if (group === null || (lineIndex === number.line && number.used.has(start + offset))) {
            fits = false;
            break;
          }
          digits += group.digits;
          real += group.real;
        }
        // At most two look-alike letters in the eight digits.
        const value = fits && real >= 6 ? calendarDate(digits) : null;
        if (value === null) continue;
        dates.push({ value, line: lineIndex, index });
        index += 1;
        start += grouping.length - 1;
        break;
      }
    }
  }
  return dates;
}

const LABELS: ReadonlyArray<readonly [DateKind, RegExp]> = [
  ["birthDate", /\bBORN\b|\bNE\s*\(?\s*E\s*\)|\bNEE\b|\bBIRTH\b|\bNAISSANCE\b/g],
  ["issueDate", /\bISS\b|\bISSUED?\b|\bDEL\b|\bDELIVRANCE\b/g],
  ["expiryDate", /\bEXP\b|\bEXPIR\w*\b/g],
];

/** The date labels on a line, in the order they are printed (BORN, then ISS/DÉL, then EXP). */
function labelsOn(line: Line): DateKind[] {
  const found: { readonly kind: DateKind; readonly at: number }[] = [];
  for (const [kind, pattern] of LABELS) {
    const first = [...line.text.matchAll(pattern)][0];
    if (first !== undefined) found.push({ kind, at: first.index });
  }
  return found.sort((left, right) => left.at - right.at).map(({ kind }) => kind);
}

/** Which date is which: by label first, then by the card's layout. */
function placeDates(found: readonly Line[], dates: readonly DateMatch[]) {
  const placed: Partial<Record<DateKind, DateMatch>> = {};
  const datesOn = (line: number) => dates.filter((date) => date.line === line);
  // A label sits on its date's line or the line above it (the card prints labels above).
  for (const [lineIndex, line] of found.entries()) {
    const kinds = labelsOn(line);
    if (kinds.length === 0) continue;
    const here = datesOn(lineIndex);
    const target = here.length > 0 ? here : datesOn(lineIndex + 1);
    if (target.length !== kinds.length) continue;
    for (const [position, kind] of kinds.entries()) {
      const date = target[position];
      if (date !== undefined && placed[kind] === undefined) placed[kind] = date;
    }
  }
  const unplaced = () =>
    dates.filter((date) => !Object.values(placed).some((taken) => taken === date));
  // Issue and expiry are printed side by side, issue first.
  if (placed.issueDate === undefined && placed.expiryDate === undefined) {
    const pairLine = [...new Set(dates.map((date) => date.line))].find(
      (line) =>
        datesOn(line).length === 2 && datesOn(line).every((date) => unplaced().includes(date)),
    );
    if (pairLine !== undefined) {
      const [issue, expiry] = datesOn(pairLine);
      if (issue !== undefined && expiry !== undefined) {
        placed.issueDate = issue;
        placed.expiryDate = expiry;
      }
    }
  }
  // The birth date comes first, above the issue and expiry dates.
  if (placed.birthDate === undefined) {
    const below = placed.issueDate?.line ?? placed.expiryDate?.line;
    const rest = unplaced();
    const birth =
      below === undefined
        ? rest.length === 3
          ? rest[0]
          : undefined
        : rest.find((date) => date.line < below);
    if (birth !== undefined) placed.birthDate = birth;
    const [, issue, expiry] = rest;
    if (below === undefined && rest.length === 3 && issue !== undefined && expiry !== undefined) {
      placed.issueDate = issue;
      placed.expiryDate = expiry;
    }
  }
  return {
    birthDate: placed.birthDate?.value,
    issueDate: placed.issueDate?.value,
    expiryDate: placed.expiryDate?.value,
  };
}

/**
 * The reading with its dates checked against each other; a date that breaks a rule was misread
 * or misplaced, and is dropped (ISO dates compare as text):
 * - a card is issued after its holder's birth, and expires after its issue;
 * - an Ontario photo card expires on its holder's birthday, so the expiry and birth dates share
 *   their month and day. When they do not, either could be the misread one, and both go.
 */
function consistent(
  healthNumber: string,
  versionCode: string | undefined,
  dates: Partial<Record<DateKind, string | undefined>>,
): HealthCardReading {
  let { birthDate, issueDate, expiryDate } = dates;
  if (issueDate !== undefined && expiryDate !== undefined && expiryDate <= issueDate) {
    expiryDate = undefined;
  }
  if (birthDate !== undefined && issueDate !== undefined && birthDate >= issueDate) {
    birthDate = undefined;
  }
  if (birthDate !== undefined && expiryDate !== undefined) {
    if (birthDate >= expiryDate || birthDate.slice(5) !== expiryDate.slice(5)) {
      birthDate = undefined;
      expiryDate = undefined;
    }
  }
  return {
    healthNumber,
    ...(versionCode === undefined ? {} : { versionCode }),
    ...(birthDate === undefined ? {} : { birthDate }),
    ...(issueDate === undefined ? {} : { issueDate }),
    ...(expiryDate === undefined ? {} : { expiryDate }),
  };
}

/**
 * Reads an Ontario health card's details out of the text the on-device reader returned. Null
 * when the text holds no single valid health number. Pure: nothing is kept or logged.
 */
export function parseHealthCardText(text: string): HealthCardReading | null {
  const found = lines(text);
  const numbers = findNumbers(found);
  const distinct = new Set(numbers.map((match) => match.healthNumber));
  const number = numbers[0];
  if (number === undefined || distinct.size !== 1) return null;
  return consistent(
    number.healthNumber,
    number.versionCode,
    placeDates(found, findDates(found, number)),
  );
}

/**
 * One reading from several passes over the same photo (the engine tries other layouts and
 * angles when one pass misses something). Null when no pass found a number, or two passes
 * found different valid numbers: the card is then too unclear to trust. Each other value is
 * kept when the passes that read it agree, and dropped when they do not; the dates are then
 * checked against each other again.
 */
export function mergeHealthCardReadings(
  readings: readonly (HealthCardReading | null)[],
): HealthCardReading | null {
  const found = readings.filter((reading): reading is HealthCardReading => reading !== null);
  const first = found[0];
  if (first === undefined || found.some((reading) => reading.healthNumber !== first.healthNumber)) {
    return null;
  }
  const agreed = (key: "versionCode" | DateKind) => {
    const votes = new Map<string, number>();
    for (const reading of found) {
      const value = reading[key];
      if (value !== undefined) votes.set(value, (votes.get(value) ?? 0) + 1);
    }
    const ranked = [...votes].sort((left, right) => right[1] - left[1]);
    const [top, runnerUp] = ranked;
    if (top === undefined) return undefined;
    if (runnerUp === undefined) return top[0];
    // Passes that disagree: only a value read at least twice, and more often than any other.
    return top[1] >= 2 && top[1] > runnerUp[1] ? top[0] : undefined;
  };
  return consistent(first.healthNumber, agreed("versionCode"), {
    birthDate: agreed("birthDate"),
    issueDate: agreed("issueDate"),
    expiryDate: agreed("expiryDate"),
  });
}

/** Only the number and version code of a reading (a pass whose layout scatters the dates). */
export function healthCardNumberOnly(reading: HealthCardReading | null): HealthCardReading | null {
  if (reading === null) return null;
  return {
    healthNumber: reading.healthNumber,
    ...(reading.versionCode === undefined ? {} : { versionCode: reading.versionCode }),
  };
}

/** Whether a reading has everything a photo card shows, so no further pass is needed. */
export function healthCardReadingComplete(reading: HealthCardReading | null): boolean {
  return (
    reading !== null &&
    reading.versionCode !== undefined &&
    reading.birthDate !== undefined &&
    reading.issueDate !== undefined &&
    reading.expiryDate !== undefined
  );
}

/** The engine's layout output, as much of it as the reader uses (tesseract.js `blocks`). */
export interface RecognizedBlock {
  readonly paragraphs: ReadonlyArray<{
    readonly lines: ReadonlyArray<{
      readonly words: ReadonlyArray<{ readonly text: string; readonly confidence: number }>;
    }>;
  }>;
}

/**
 * A word the engine is less sure of than this (0 to 100) is left out. On synthetic cards the
 * words read right scored about 75 to 99, and a misread date about 30.
 */
export const MIN_WORD_CONFIDENCE = 60;

/** A word with nothing the parser could take for a letter or digit: a dash, a dot, a colon. */
const SEPARATOR_ONLY = /^[^\p{L}\p{N}|!]*$/u;

/**
 * The recognized text, one line per line, keeping only the words the engine is confident of.
 * An unsure word becomes a gap ("?"), so the words around it never join into a number or date.
 * An unsure separator (the faint dashes between the number's groups) is simply left out, as a
 * sure one is: it could never have been a digit.
 */
export function confidentText(blocks: readonly RecognizedBlock[]): string {
  return blocks
    .flatMap((block) => block.paragraphs)
    .flatMap((paragraph) => paragraph.lines)
    .map((line) =>
      line.words
        .flatMap((word) =>
          word.confidence >= MIN_WORD_CONFIDENCE
            ? [word.text]
            : SEPARATOR_ONLY.test(word.text)
              ? []
              : ["?"],
        )
        .join(" "),
    )
    .join("\n");
}

/** The coverage answers a card reading may fill. */
export type HealthCardFillField = "healthNumber" | "versionCode" | "expiryDate";

export interface HealthCardFill {
  /** The empty coverage answers the card fills in. An answer the patient typed is never here. */
  readonly values: Partial<Pick<IntakeData, HealthCardFillField>>;
  /**
   * The patient typed a health number that is not the card's. Nothing is filled then, so the
   * answers never mix two cards (or a typo with the card's version code and expiry).
   */
  readonly numberDiffers: boolean;
  /** The card's birth date is not the date of birth the patient gave (or the link verified). */
  readonly birthDateDiffers: boolean;
}

/** The number as printed on the card (1234 567 890), easy to check against it. */
function printedNumber(digits: string): string {
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
}

/**
 * What a reading fills in: only the Ontario card's empty number, version code and expiry date.
 * Never another field, never a value the patient typed, and never an identity answer (the birth
 * date is only compared). Nothing at all beside a typed number that is not the card's. The
 * expiry date is filled only when it falls on the birthday the patient gave, as an Ontario photo
 * card's does: one that does not is misread, or not theirs.
 */
export function healthCardFill(draft: IntakeData, reading: HealthCardReading): HealthCardFill {
  const given = draft.dateOfBirth.trim();
  const birthDateDiffers =
    given !== "" && reading.birthDate !== undefined && given !== reading.birthDate;
  const typed = draft.healthNumber.trim();
  const numberDiffers =
    draft.coverageChoice === "ohip" &&
    typed !== "" &&
    normalizeOntarioHealthNumber(typed) !== reading.healthNumber;
  const values: { -readonly [K in HealthCardFillField]?: string } = {};
  if (draft.coverageChoice === "ohip" && !numberDiffers) {
    if (typed === "") values.healthNumber = printedNumber(reading.healthNumber);
    if (draft.versionCode.trim() === "" && reading.versionCode !== undefined) {
      values.versionCode = reading.versionCode;
    }
    const expiry = reading.expiryDate;
    if (
      draft.expiryDate.trim() === "" &&
      expiry !== undefined &&
      (given === "" || expiry.slice(5) === given.slice(5))
    ) {
      values.expiryDate = expiry;
    }
  }
  return { values, numberDiffers, birthDateDiffers };
}
